import {
  NexoyaContentRuleFunnelStepMapping,
  NexoyaFunnelStepMappingInput,
  NexoyaFunnelStepMappingType,
  NexoyaFunnelStepV2,
  NexoyaMeasurement,
  NexoyaTranslation,
} from '../../../../../types';
import { cleanSearchDimensions } from '../../../../../components-ui/filter/utils';
import translate from '../../../../../utils/translate';
import { AssignedMetric } from './ContentMetricAssignment';
import { getSelectedMeasurementForFunnelStepId } from '../../../../../lib/metric-assignment';
import {
  SNOWFLAKE_PROVIDER_ID,
  SNOWFLAKE_UI_MAPPING_TYPE,
  getUiMappingType,
  resolveProviderIdForSubmission,
} from './assignment/constants';

function getTrackingLabel(providerId?: number | null): string {
  if (providerId === SNOWFLAKE_PROVIDER_ID) return 'Snowflake tracking';
  // All legacy Analytics mappings without providerId are GA4
  return 'GA4 tracking';
}

export type PortfolioRuleType = 'content-rule' | 'impact-group-rule' | 'attribution-rule';

export const getAssignedMetricBasedOnMappingType = ({
  funnelStepId,
  assignedMetrics,
  mergedMeasurements,
  translations,
  funnelStepName,
}: {
  funnelStepId: number;
  assignedMetrics: AssignedMetric[];
  mergedMeasurements?: NexoyaMeasurement[];
  translations: NexoyaTranslation[];
  funnelStepName?: string;
}) => {
  const assignedMetric = assignedMetrics.find((metric) => metric.funnelStepId === funnelStepId);
  const selectedMeasurement = getSelectedMeasurementForFunnelStepId(
    funnelStepId,
    mergedMeasurements || [],
    assignedMetrics,
  );
  const assignedMetricName = translate(translations, selectedMeasurement?.name);

  switch (assignedMetric?.type) {
    case NexoyaFunnelStepMappingType.Metric:
      return {
        assignedMetricId: selectedMeasurement?.measurement_id,
        assignedMetricName,
        mappingTypeLabel: 'Assign metric',
        type: assignedMetric?.type,
        hasAssigned: !!selectedMeasurement,
      };
    case NexoyaFunnelStepMappingType.Conversion:
      return {
        assignedMetricId: assignedMetric?.metricId,
        assignedMetricName,
        mappingTypeLabel: 'Custom conversions',
        type: assignedMetric?.type,
        hasAssigned: assignedMetric.conversions.some(
          (conversion) => conversion.metricId && conversion.accountConversionIds?.length,
        ),
      };
    case NexoyaFunnelStepMappingType.Analytics: {
      const trackingLabel = getTrackingLabel(assignedMetric?.providerId);
      return {
        assignedMetricId: assignedMetric?.metricId,
        assignedMetricName: trackingLabel,
        mappingTypeLabel: trackingLabel,
        type: assignedMetric?.type,
        hasAssigned: !!(
          assignedMetric.searchDimensions?.length &&
          assignedMetric?.metricId &&
          assignedMetric.accountContentId
        ),
      };
    }
    case NexoyaFunnelStepMappingType.CustomMetric:
      return {
        assignedMetricId: assignedMetric?.metricId,
        assignedMetricName: 'Custom metric',
        mappingTypeLabel: 'Custom metric',
        type: assignedMetric?.type,
        hasAssigned: !!(assignedMetric?.searchTitle && assignedMetric?.metricId),
      };
    case NexoyaFunnelStepMappingType.CustomImport:
      return {
        assignedMetricId: assignedMetric?.metricId,
        assignedMetricName: 'Custom import',
        mappingTypeLabel: 'Custom import',
        type: assignedMetric?.type,
        hasAssigned: !!(assignedMetric?.searchTitle && assignedMetric?.metricId),
      };
    case NexoyaFunnelStepMappingType.CalculatedMetric:
      return {
        assignedMetricId: assignedMetric?.metricId,
        assignedMetricName:
          assignedMetric?.searchTitle || (funnelStepName ? `Calculated ${funnelStepName}` : 'Calculated metric'),
        mappingTypeLabel: 'Calculated metric',
        type: assignedMetric?.type,
        hasAssigned: !!assignedMetric?.calculatedMetricFormula?.length,
      };
    case NexoyaFunnelStepMappingType.Ignore:
      return {
        assignedMetricId: null,
        assignedMetricName: 'No metric',
        mappingTypeLabel: 'Ignore mapping',
        type: assignedMetric?.type,
        hasAssigned: true,
      };
    default:
      return {
        assignedMetricId: null,
        assignedMetricName: '',
        mappingTypeLabel: '',
        type: null,
        hasAssigned: false,
      };
  }
};

export const getHumanReadableFunnelStepMapping = ({
  funnelStepMapping,
  measurements,
  translations,
  funnelStepName,
}: {
  funnelStepMapping: NexoyaContentRuleFunnelStepMapping;
  measurements: NexoyaMeasurement[];
  translations: NexoyaTranslation[];
  funnelStepName?: string;
}): { metric: string; mappingType: string } => {
  const measurement = measurements?.find((m) => m?.measurement_id === funnelStepMapping?.mapping?.metricId);
  const assignedMetricName = translate(translations, measurement?.name);

  if (funnelStepMapping?.mapping?.type === NexoyaFunnelStepMappingType.Metric) {
    return { metric: assignedMetricName, mappingType: 'Assign metric' };
  }

  if (funnelStepMapping?.mapping?.type === NexoyaFunnelStepMappingType.Conversion) {
    return { metric: 'Multiple custom conversions', mappingType: 'Custom conversions' };
  }

  if (funnelStepMapping?.mapping?.type === NexoyaFunnelStepMappingType.Analytics) {
    return { metric: assignedMetricName, mappingType: getTrackingLabel(funnelStepMapping.mapping.providerId) };
  }

  if (funnelStepMapping?.mapping?.type === NexoyaFunnelStepMappingType.CustomImport) {
    return { metric: 'Custom import', mappingType: 'Custom import' };
  }

  if (funnelStepMapping?.mapping?.type === NexoyaFunnelStepMappingType.CustomMetric) {
    return { metric: 'Custom metric', mappingType: 'Custom metric' };
  }

  if (funnelStepMapping?.mapping?.type === NexoyaFunnelStepMappingType.CalculatedMetric) {
    return {
      metric:
        funnelStepMapping.mapping.searchTitle ||
        (funnelStepName ? `Calculated ${funnelStepName}` : 'Calculated metric'),
      mappingType: 'Calculated metric',
    };
  }

  if (funnelStepMapping?.mapping?.type === NexoyaFunnelStepMappingType.Ignore) {
    return { metric: undefined, mappingType: 'Ignore mapping' };
  }

  return { metric: 'No assignment', mappingType: undefined };
};

export type AnalyticsMappingValidationError =
  | { kind: 'missing-metric'; funnelStepId: number }
  | { kind: 'missing-property'; funnelStepId: number }
  | { kind: 'missing-dimensions'; funnelStepId: number };

/**
 * The BE requires searchDimensions, metricId and accountContentId for ANALYTICS mappings but
 * answers with a generic error that never says which funnel step is incomplete.
 * providerId is skipped — `resolveProviderIdForSubmission` defaults it to GA4. accountContentId
 * uses `!= null` so 0 and -1 (Snowflake sentinel) pass. Returns the first invalid step.
 */
export function findInvalidAnalyticsMapping(
  metrics: ReadonlyArray<AssignedMetric>,
): AnalyticsMappingValidationError | null {
  for (const m of metrics) {
    if (m?.type !== NexoyaFunnelStepMappingType.Analytics) continue;

    const funnelStepId = m.funnelStepId;

    if (!m.metricId) {
      return { kind: 'missing-metric', funnelStepId };
    }
    if (m.accountContentId == null) {
      return { kind: 'missing-property', funnelStepId };
    }
    const hasUsableDimension = m.searchDimensions?.some((d) => d?.dimensionName && d.values?.some((v) => v?.trim()));
    if (!hasUsableDimension) {
      return { kind: 'missing-dimensions', funnelStepId };
    }
  }
  return null;
}

export type MetricCompatibilityError = {
  kind: 'metric-step-type-mismatch';
  funnelStepId: number;
  metricId: number;
  funnelStepType: string;
};

/**
 * Enforces the BE rule that an assigned metric's `optimization_target_type` must include the funnel
 * step's type, so legacy mappings whose metric no longer fits the step fail with a clear UI message.
 * IGNORE / CALCULATED_METRIC / CONVERSION are skipped (no single validated metricId) as is orphan
 * metric/step data. Returns the first mismatch.
 */
export function findIncompatibleMetricMapping(
  metrics: ReadonlyArray<AssignedMetric>,
  funnelSteps: ReadonlyArray<{ funnelStepId: number; type: string }>,
  measurements: ReadonlyArray<{ measurement_id?: number | null; optimization_target_type?: string[] | null }>,
): MetricCompatibilityError | null {
  for (const m of metrics) {
    if (!m?.type || !m.metricId) continue;
    if (
      m.type === NexoyaFunnelStepMappingType.Ignore ||
      m.type === NexoyaFunnelStepMappingType.CalculatedMetric ||
      m.type === NexoyaFunnelStepMappingType.Conversion
    ) {
      continue;
    }

    const step = funnelSteps.find((s) => s.funnelStepId === m.funnelStepId);
    if (!step?.type) continue;

    const measurement = measurements.find((meas) => meas.measurement_id === m.metricId);
    if (!measurement) continue;

    const targets = measurement.optimization_target_type ?? [];
    if (!targets.includes(step.type)) {
      return {
        kind: 'metric-step-type-mismatch',
        funnelStepId: m.funnelStepId,
        metricId: m.metricId,
        funnelStepType: step.type,
      };
    }
  }
  return null;
}

export const areAllFunnelStepsAssigned = (
  funnelSteps: NexoyaFunnelStepV2[],
  assignedMetrics: AssignedMetric[],
  mergedMeasurements: NexoyaMeasurement[],
  translations: NexoyaTranslation[],
) => {
  return funnelSteps.every((step) => {
    const stepResult = getAssignedMetricBasedOnMappingType({
      funnelStepId: step.funnelStepId,
      assignedMetrics,
      mergedMeasurements,
      translations,
    });

    return stepResult.hasAssigned;
  });
};

export const areSomeFunnelStepsAssigned = (
  funnelSteps: NexoyaFunnelStepV2[],
  assignedMetrics: AssignedMetric[],
  mergedMeasurements: NexoyaMeasurement[],
  translations: NexoyaTranslation[],
) => {
  return funnelSteps.some((step) => {
    const stepResult = getAssignedMetricBasedOnMappingType({
      funnelStepId: step.funnelStepId,
      assignedMetrics,
      mergedMeasurements,
      translations,
    });

    return stepResult.hasAssigned;
  });
};

/**
 * Pure helper: given a metric entry and a mapping-type change,
 * returns the updated metric with reset fields appropriate to the new type.
 */
export function buildMetricForTypeChange(
  metric: AssignedMetric,
  type: NexoyaFunnelStepMappingType | typeof SNOWFLAKE_UI_MAPPING_TYPE,
): AssignedMetric {
  if (type === SNOWFLAKE_UI_MAPPING_TYPE) {
    return {
      ...metric,
      type: NexoyaFunnelStepMappingType.Analytics,
      accountContentId: null,
      providerId: SNOWFLAKE_PROVIDER_ID,
      metricId: null,
      searchTitle: '',
      conversions: [],
      searchDimensions: [],
      calculatedMetricFormula: null,
    };
  }

  const updatedMetric = { ...metric, type, calculatedMetricFormula: null };

  if (type === NexoyaFunnelStepMappingType.Metric) {
    return { ...updatedMetric, providerId: null, conversions: [], searchDimensions: [] };
  }
  if (type === NexoyaFunnelStepMappingType.Ignore) {
    return { ...updatedMetric, providerId: null, metricId: null, conversions: [], searchDimensions: [] };
  }
  if (type === NexoyaFunnelStepMappingType.Conversion) {
    return {
      ...updatedMetric,
      providerId: null,
      metricId: null,
      conversions: [{ accountConversionIds: [], conversionName: '', metricId: null }],
      searchDimensions: [],
    };
  }
  if (type === NexoyaFunnelStepMappingType.CustomImport) {
    return {
      ...updatedMetric,
      providerId: null,
      metricId: null,
      searchTitle: '',
      conversions: [],
      searchDimensions: [],
    };
  }
  if (type === NexoyaFunnelStepMappingType.Analytics) {
    return {
      ...updatedMetric,
      accountContentId: null,
      providerId: null,
      metricId: null,
      searchTitle: '',
      conversions: [],
      searchDimensions: [],
    };
  }
  if (type === NexoyaFunnelStepMappingType.CustomMetric) {
    return {
      ...updatedMetric,
      providerId: null,
      metricId: null,
      searchTitle: '',
      conversions: [],
      searchDimensions: [],
    };
  }
  if (type === NexoyaFunnelStepMappingType.CalculatedMetric) {
    return {
      ...updatedMetric,
      metricId: null,
      accountContentId: null,
      providerId: null,
      conversions: [],
      searchDimensions: [],
      calculatedMetricFormula: metric.calculatedMetricFormula ?? null,
    };
  }

  return updatedMetric;
}

export type TypeStash = Record<string, AssignedMetric>;

/**
 * Pure helper: applies a mapping-type change while preserving earlier assignments.
 * Stashes the outgoing mapping under its current UI type so that if the user
 * toggles back, the prior configuration is restored instead of silently wiped.
 */
export function applyMappingTypeChange(
  metric: AssignedMetric,
  type: NexoyaFunnelStepMappingType | typeof SNOWFLAKE_UI_MAPPING_TYPE,
  stash: TypeStash,
): { metric: AssignedMetric; stash: TypeStash } {
  const targetKey = String(type);
  const currentKey = metric.type ? String(getUiMappingType(metric.type, metric.providerId)) : null;

  if (currentKey === targetKey) return { metric, stash };

  const nextStash: TypeStash = currentKey ? { ...stash, [currentKey]: metric } : stash;
  const restored = nextStash[targetKey];

  return {
    metric: restored ?? buildMetricForTypeChange(metric, type),
    stash: nextStash,
  };
}

/**
 * Pure helper: builds the funnelStepMappings payload from assigned metrics.
 * Shared by both handleUpdate and handleSubmit.
 */
export function buildFunnelStepMappings(
  assignedMetrics: AssignedMetric[],
): { funnelStepId: number; mapping: NexoyaFunnelStepMappingInput }[] {
  return (
    assignedMetrics
      ?.filter((m): m is NonNullable<typeof m> => Boolean(m?.type))
      ?.map(
        ({
          funnelStepId,
          metricId,
          conversions,
          searchDimensions,
          type,
          searchTitle,
          accountContentId,
          providerId,
          calculatedMetricFormula,
        }) => {
          const resolvedProviderId = resolveProviderIdForSubmission(type, providerId);
          // FunnelStepMappingConversionInput.metricId is Int!, so a half-configured conversion (goal
          // picked, no metric yet) fails the WHOLE save; drop those so the complete ones still persist.
          const completeConversions = (conversions ?? []).filter(
            (conversion) => conversion.metricId && conversion.accountConversionIds?.length,
          );
          const mapping: NexoyaFunnelStepMappingInput = {
            type,
            ...(metricId && { metricId }),
            ...(searchTitle && { searchTitle }),
            ...(accountContentId != null && { accountContentId }),
            ...(resolvedProviderId && { providerId: resolvedProviderId }),
            ...(completeConversions.length && {
              conversions: completeConversions.map(({ accountConversionIds, metricId, conversionName }) => ({
                metricId,
                conversionName,
                accountConversionIds,
              })),
            }),
            ...(calculatedMetricFormula && { calculatedMetricFormula }),
          };

          if (searchDimensions?.length) {
            mapping.searchDimensions = [...cleanSearchDimensions(searchDimensions)];
          }

          return { funnelStepId: Number(funnelStepId), mapping };
        },
      ) ?? []
  );
}
